import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { LoadStatus } from '@capital-access/common/utils';
import { SimpleActionableEmptyStatesOptions } from '@capital-access/firefly/components';
import { ProfileType } from '@capital-access/profiles/common';
import {
  EXPORT_REPORT_INCLUDED_CONTACTS,
  EXPORT_REPORT_MODULE_NAME,
  EXPORT_REPORT_SELECTED_PROFILES,
  MAX_TEMPLATES_PER_ACCOUNT,
  ReportTemplate,
  ReportTemplateFormType,
  TemplateLibraryService
} from '@capital-access/reporting/common';
import { loadAllTemplates, openCreateReportingTemplateDrawer } from '@capital-access/reporting/template-library';
import { NavigationIntentService } from '@capital-access/reporting/template-library';
import { LOCALIZATION_KEY } from '../../../constants';
import { ReportTypeMapperService } from '../../services/report-type-mapper.service';
import { TemplatesStore } from '../../services/templates.store';

@Component({
  selector: 'ca-configurable-templates',
  templateUrl: './configurable-templates.component.html',
  styleUrls: ['./configurable-templates.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfigurableTemplatesComponent implements OnInit {
  @Input() request?: any;
  @Input() setAsDefaultControl!: FormControl;
  private totalTemplateCount = 0;
  private isDisabled = false;
  readonly templatesStore = inject(TemplatesStore);
  private readonly templateLibraryService = inject(TemplateLibraryService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  readonly profileType = input.required<ProfileType>();
  readonly LoadStatus = LoadStatus;
  readonly ProfileType = ProfileType;
  readonly MAX_TEMPLATES_PER_ACCOUNT = MAX_TEMPLATES_PER_ACCOUNT;
  readonly localizationScope = LOCALIZATION_KEY;
  readonly SimpleActionableEmptyStatesOptions = SimpleActionableEmptyStatesOptions;
  readonly store = inject(Store);
  readonly reportTypeMapper = inject(ReportTypeMapperService);
  readonly navigationIntent = inject(NavigationIntentService) as NavigationIntentService;
  readonly reportType = () => (this.profileType() === ProfileType.Contact ? 'contact' : 'investor');

  createTemplate() {
    if (this.request?.profiles?.length) {
      const profiles = this.request!.profiles?.map(
        (p: { id: { prId: string | number; crmId: string | number }; name?: string }) => ({
          prId: p.id.prId,
          crmId: p.id.crmId,
          name: p.name ?? '',
          profileType: this.request!.profileType,
          reportedQuarters: this.request!.reportedQuarters ?? []
        })
      );
      localStorage.setItem(EXPORT_REPORT_SELECTED_PROFILES, JSON.stringify(profiles));
      if (this.request?.moduleName) {
        localStorage.setItem(EXPORT_REPORT_MODULE_NAME, this.request?.moduleName);
      }

      if (this.request?.includedContacts?.length) {
        localStorage.setItem(EXPORT_REPORT_INCLUDED_CONTACTS, JSON.stringify(this.request?.includedContacts));
      }
      const mappedReportType = this.reportTypeMapper.mapProfileTypeToReportType(this.profileType());
      this.navigationIntent.setOpenInNewTab(true);
      this.store.dispatch(loadAllTemplates({ silentRefresh: false }));
      this.store.dispatch(
        openCreateReportingTemplateDrawer({
          reportType: mappedReportType,
          actionType: ReportTemplateFormType.create
        })
      );
    }
  }

  onTemplateSelected(template: ReportTemplate): void {
    this.templatesStore.setSelectedTemplate(template);
    this.setAsDefaultControl.setValue(false, { emitEvent: false });
  }

  ngOnInit() {
    this.templateLibraryService.getTemplates().subscribe((templates: ReportTemplate[]) => {
      const teamTemplates = templates.filter((t: ReportTemplate) => !t.caTemplateInd);
      this.totalTemplateCount = teamTemplates.length;

      this.isDisabled = this.totalTemplateCount >= MAX_TEMPLATES_PER_ACCOUNT;

      this.changeDetectorRef.markForCheck();
    });
  }

  get isCreateTemplateDisabled() {
    return this.templatesStore.myTeamTemplateCount() >= MAX_TEMPLATES_PER_ACCOUNT || this.isDisabled;
  }
}
